import { BlobProvider, PDFDownloadLink } from '@react-pdf/renderer'
import { message } from 'antd'
import Checkbox from 'components/CustomCheckbox'
import ErrorMessage from 'components/ErrorMessage'
import DataConfirmationModal from 'components/Modals/dataConfirmation/'
import ReInvestStepper from 'components/ReInvestStepper'
import SubscriptionAgreemenPdfRender from 'components/SubscriptionPdfRenderer'
import SubscriptionAgreemenPdf from 'components/SubscriptionToPdf'
import { SUBSCRIPTION_AGREEMENT_NOT_RETIREMENT, SUBSCRIPTION_AGREEMENT_RETIREMENT } from 'constants/accreditation'
import React, { useEffect, useState } from 'react'
import { isMobile } from 'react-device-detect'
import { Margin, usePDF } from 'react-to-pdf'
import { getEntityType } from 'utils'
import CompanyLogo from '../../../assets/afbc.png'
import fileWordIcon from '../../../assets/images/file-word.svg'
import Side8 from '../../../assets/investing-signup-images/can_3976_2.jpeg'
import Button from '../../../components/Button'
import LoaderModal from '../../../components/Modals/LoaderModal'
import SubscriptionAgreementModal from '../../../components/Modals/SubscriptionAgreementModal'
import { IFormProps, InvestingPersonEnum } from '../../../interfaces'

const AcknowledgementPage = (props: IFormProps) => {
  const [isLoading, setIsLoading] = useState(false)
  const [showSubscriptionAgreement, setShowSubscriptionAgreement] = useState(false)
  const [selected, setSelected] = useState({
    agreement1: {
      statement: '',
      agreed: 'false',
    },
    agreement2: {
      statement: '',
      agreed: 'false',
    },
    agreement3: {
      statement: '',
      agreed: 'false',
    },
    agreement4: {
      statement: '',
      agreed: 'false',
    },
  })
  const [isDisabled, setIsDisabled] = useState(true)
  const [isError, setIsError] = useState(false)
  const { toPDF, targetRef } = usePDF({
    filename: 'subcription-agreement.pdf',
    page: { margin: Margin.MEDIUM },
    canvas: { qualityRatio: 2 },
  })
  const [downloadingPdf, setDownloadingPdf] = useState(false)
  const [showDataConfirmationModal, setShowDataConfirmationModal] = useState(false)

  const handleSubmit = async () => {
    if (validatePage(selected)) {
      setIsError(true)
    } else {
      setIsError(false)
      setIsLoading(true)
      props.setValue('investingProfile.selfAccreditation2', selected)
      props.nextStep('4')
    }
  }

  type AgreementKey = 'agreement1' | 'agreement2' | 'agreement3' | 'agreement4'

  const AGREEMENT_MAP: Record<string, AgreementKey> = {
    item1: 'agreement1',
    item2: 'agreement2',
    item3: 'agreement3',
    item4: 'agreement4',
  }

  const handleSelection = (e: any) => {
    const id = e.target.id as string

    if (id in AGREEMENT_MAP) {
      const key = AGREEMENT_MAP[id] as AgreementKey
      const newAgreed = selected[key].agreed === 'false' ? 'true' : 'false'

      const isRetirement = props.getValues('investingProfile.option') === InvestingPersonEnum.RETIREMENT
      const statementEnum = isRetirement ? SUBSCRIPTION_AGREEMENT_RETIREMENT : SUBSCRIPTION_AGREEMENT_NOT_RETIREMENT
      const statementKey = id.toUpperCase() as keyof typeof statementEnum
      const statementValue = statementEnum[statementKey]

      setSelected((prevSelected) => {
        const updatedSelected = {
          ...prevSelected,
          [key]: { statement: statementValue, agreed: newAgreed },
        }

        if (id === 'item1') {
          ;['ITEM2', 'ITEM3', 'ITEM4'].forEach((itemKey) => {
            updatedSelected[AGREEMENT_MAP[itemKey.toLowerCase()]] = {
              statement: statementEnum[itemKey as keyof typeof statementEnum],
              agreed: newAgreed,
            }
          })
        }

        props.setValue('investingProfile.selfAccreditation2', updatedSelected)

        return updatedSelected
      })
    }
  }

  const onDownloadPdf = () => {
    if (isMobile) {
      message.warning('Download PDF is temporary unavailable in mobile only. Download in desktop.')
      return
    }
    setDownloadingPdf(true)
    setTimeout(() => {
      toPDF()
      setDownloadingPdf(false)
    }, 1000)
  }

  const validatePage = (selected: any) => {
    let items = Object.keys(AGREEMENT_MAP)
      .filter((key) => key !== 'item1')
      .map((key) => {
        const agreementKey = AGREEMENT_MAP[key] as AgreementKey
        return selected[agreementKey].agreed
      })

    if (items.includes('false')) {
      return true
    } else {
      return false
    }
  }

  useEffect(() => {
    setIsError(false)
    validatePage(selected) === true ? setIsDisabled(true) : setIsDisabled(false)
  }, [selected]) // eslint-disable-line

  useEffect(() => {
    if (props.getValues('investingProfile.selfAccreditation2')) {
      setSelected(props.getValues('investingProfile.selfAccreditation2'))
    }
  }, []) // eslint-disable-line

  return (
    <div className="flex h-screen w-full flex-col md:flex-row md:justify-start">
      <div className="mt-10 flex flex-col sm:w-screen md:mt-0 md:min-h-screen md:w-[45%] lg:w-[40%] xl:w-[35%]">
        <div className="relative w-screen px-10 sm:h-auto sm:px-16 md:hidden">
          <img className="w-screen" src={CompanyLogo} alt="CompanyLogo" />
        </div>
        <div className="relative hidden flex-grow md:flex">
          <img className="flex h-screen w-full" src={Side8} alt="Side8" />
        </div>
      </div>

      <div className="flex flex-col p-10 sm:w-[100%] md:w-[55%] lg:h-screen lg:w-[60%] lg:overflow-y-scroll xl:w-[65%]">
        <div className="md:my-3">
          <ReInvestStepper current={Number(props.currentStep)} />
        </div>

        <div className="py-8 text-left text-2xl font-semibold text-gray-900 md:py-12 md:pb-10 md:text-4xl">
          Acknowledgements
        </div>

        <div className="flex flex-col md:pr-4">
          <div className="mb-8 flex w-full flex-col gap-6 rounded-md border border-gray-400 p-6 md:w-[60%] md:border-2 md:border-gray-200">
            <div className="flex items-center justify-start gap-4">
              <img alt="aggreement img" src={fileWordIcon} />
              <span className="my-0 text-xl font-semibold">Subscription Agreement</span>
            </div>
            <div className="flex items-center justify-start gap-4">
              {!isMobile ? (
                <>
                  <PDFDownloadLink
                    document={<SubscriptionAgreemenPdfRender getValues={props.getValues} />}
                    fileName="subscription-agreement.pdf"
                    className="flex h-[40px] items-center rounded-md border border-gray-400 px-4 py-2 text-base md:border-gray-200"
                  >
                    Download PDF
                  </PDFDownloadLink>
                  <button
                    className="flex h-[40px] items-center rounded-md border border-gray-400 px-4 py-2 text-base md:border-gray-200"
                    onClick={() => {
                      setShowSubscriptionAgreement(true)
                    }}
                  >
                    View
                  </button>
                </>
              ) : (
                <BlobProvider document={<SubscriptionAgreemenPdfRender getValues={props.getValues} />}>
                  {({ url, ...rest }) => {
                    return (
                      <a
                        href={url as string}
                        target="_blank"
                        className="flex h-[40px] items-center rounded-md border border-gray-400 px-4 py-2 text-base md:border-gray-200"
                      >
                        Download/View PDF
                      </a>
                    )
                  }}
                </BlobProvider>
              )}
            </div>
          </div>
          <div className="flex gap-1 py-1 text-base font-semibold md:font-bold">
            <Checkbox
              label=""
              isChecked={selected.agreement1.agreed === 'true'}
              id="item1"
              value={'true'}
              onChange={(e) => handleSelection(e)}
            />

            {props.getValues('investingProfile.option') === InvestingPersonEnum.RETIREMENT &&
              `By checking this box, I, as the authorized representative of the ${getEntityType(
                props.getValues('investingProfile.retirementAccountType'),
              )}, confirm that I have read, understood, and agreed to all the statements below. I also confirm that I am signing on behalf of the ${getEntityType(
                props.getValues('investingProfile.retirementAccountType'),
              )} and that the I, as an authorized representative of the ${getEntityType(
                props.getValues('investingProfile.retirementAccountType'),
              )}, agree to have my signature electronically affixed to the subscription agreement and any related documents.`}
            {props.getValues('investingProfile.option') === InvestingPersonEnum.FOR_MYSELF &&
              'By checking this box, I agree that I have read, I understand and I agree with all statements below and I also agree to have my signature electronically signed to the subscription agreement and any related documents.'}
            {props.getValues('investingProfile.option') === InvestingPersonEnum.IN_BEHALF_OF_AN_ENTITY &&
              'By checking this box, the company and I agree that we have read, we understand and we agree with all statements below and the company and I also agree to have our signatures electronically signed to the subscription agreement and any related documents.'}
            {props.getValues('investingProfile.option') === InvestingPersonEnum.JOINTLY &&
              'By checking this box, we agree that we have read, we understand and we agree with all statements below and we also agree to have our signatures electronically signed to the subscription agreement and any related documents.'}
          </div>

          <div className="flex gap-1 py-1 text-base">
            <Checkbox
              label=""
              isChecked={selected.agreement2.agreed === 'true'}
              id="item2"
              value={'true'}
              onChange={(e) => handleSelection(e)}
            />
            {props.getValues('investingProfile.option') === InvestingPersonEnum.RETIREMENT &&
              `By checking this box, I, as the authorized representative of the ${getEntityType(
                props.getValues('investingProfile.retirementAccountType'),
              )}, acknowledge that I have read and understood the terms of the following documents: Subscription Agreement, Privacy Policy, and Terms of Use, including their Electronic Signature Policy.`}
            {props.getValues('investingProfile.option') === InvestingPersonEnum.FOR_MYSELF &&
              'By checking this box, I acknowledge that I have read and understand each of the following and agree to all terms as set out in each of the following: Subscription Agreement, Privacy Policy, and Terms of Use including the Electronic Signature Policy contained therein.'}
            {props.getValues('investingProfile.option') === InvestingPersonEnum.IN_BEHALF_OF_AN_ENTITY &&
              'By checking this box, the company and I acknowledge that we have read and understand each of the following and the company and I agree to all terms as set out in each of the following: Subscription Agreement, Privacy Policy, and Terms of Use including the Electronic Signature Policy contained therein.'}
            {props.getValues('investingProfile.option') === InvestingPersonEnum.JOINTLY &&
              'By checking this box, we acknowledge that we have read and understand each of the following and agree to all terms as set out in each of the following: Subscription Agreement, Privacy Policy, and Terms of Use including the Electronic Signature Policy contained therein.'}
          </div>

          <div className="flex gap-1 py-1 text-base">
            <Checkbox
              label=""
              isChecked={selected.agreement3.agreed === 'true'}
              id="item3"
              value={'true'}
              onChange={(e) => handleSelection(e)}
            />
            {props.getValues('investingProfile.option') === InvestingPersonEnum.RETIREMENT &&
              `By checking this box, I, as the authorized representative of the ${getEntityType(
                props.getValues('investingProfile.retirementAccountType'),
              )}, grant consent to ${
                process.env.REACT_APP_ISSUER_NAME
              }, and their broker-dealer Cultivate Capital Group LLC, and assigns, to contact me and/or the trust through email, text message/SMS, and/or telephone as necessary to obtain information for finalizing or confirming the ${getEntityType(
                props.getValues('investingProfile.retirementAccountType'),
              )}'s investment or for marketing purposes.`}
            {props.getValues('investingProfile.option') === InvestingPersonEnum.FOR_MYSELF &&
              'By checking this box, I consent to Armed Forces Brewing Company, Inc. and their broker-dealer and assigns contacting me via e-mail, direct messaging through social media, text message/SMS and/or telephone as needed to obtain information from me to finalize or reconfirm my investment or for marketing purposes.'}
            {props.getValues('investingProfile.option') === InvestingPersonEnum.IN_BEHALF_OF_AN_ENTITY &&
              'By checking this box, the company and I consent to Armed Forces Brewing Company, Inc. and their broker-dealer and assigns contacting us via e-mail, direct messaging through social media, text message/SMS and/or telephone as needed to obtain information from us to finalize or reconfirm the company’s investment or for marketing purposes.'}
            {props.getValues('investingProfile.option') === InvestingPersonEnum.JOINTLY &&
              'By checking this box, we consent to Armed Forces Brewing Company, Inc. and their broker-dealer and assigns contacting us via e-mail, direct messaging through social media, text message/SMS and/or telephone as needed to obtain information from us to finalize or reconfirm our investment or for marketing purposes.'}
          </div>

          <div className="flex gap-1 py-1 text-base">
            <Checkbox
              label=""
              isChecked={selected.agreement4.agreed === 'true'}
              id="item4"
              value={'true'}
              onChange={(e) => handleSelection(e)}
            />
            {props.getValues('investingProfile.option') === InvestingPersonEnum.RETIREMENT &&
              `By checking this box, I, as the authorized representative of the ${getEntityType(
                props.getValues('investingProfile.retirementAccountType'),
              )}, certify, under penalty of perjury, that I have provided the correct taxpayer identification number for the ${getEntityType(
                props.getValues('investingProfile.retirementAccountType'),
              )}. I also certify that I am a US citizen, US resident, or another US person, and that the ${getEntityType(
                props.getValues('investingProfile.retirementAccountType'),
              )} is exempt from backup withholding.`}
            {props.getValues('investingProfile.option') === InvestingPersonEnum.FOR_MYSELF &&
              ' By checking this box, under penalty of perjury, I certify that I have provided my correct taxpayer identification number, and that I am a US citizen, US resident, or other US person and that I am exempt from backup withholding.'}
            {props.getValues('investingProfile.option') === InvestingPersonEnum.IN_BEHALF_OF_AN_ENTITY &&
              'By checking this box, under penalty of perjury, we certify that we have provided correct taxpayer identification numbers.'}
            {props.getValues('investingProfile.option') === InvestingPersonEnum.JOINTLY &&
              ' By checking this box, under penalty of perjury, we certify that we have provided our correct taxpayer identification numbers, and that we are both US citizens, US residents, or other US persons and that we are both exempt from backup withholding.'}
          </div>
        </div>
        {downloadingPdf && (
          <div ref={targetRef}>
            <SubscriptionAgreemenPdf getValues={props.getValues} />
          </div>
        )}

        {isError && (
          <div className="mt-4">
            <ErrorMessage message={'You must select all of the boxes to proceed with your investment'} />
          </div>
        )}

        <div className="w-full justify-center gap-3 py-10 sm:block md:flex">
          <div className="flex flex-col sm:w-[100%] md:w-[20%]">
            <Button btnName={'Back'} onClickHandler={() => props.previousStep('2')} />
          </div>
          <div className="flex flex-col sm:w-[100%] md:w-[80%]">
            <Button
              btnName={'Next'}
              btnType={'button'}
              onClickHandler={() => setShowDataConfirmationModal(true)}
              disabled={isDisabled}
            />
          </div>
        </div>
      </div>
      <LoaderModal isVisible={isLoading || downloadingPdf} />
      <SubscriptionAgreementModal
        isVisible={showSubscriptionAgreement}
        setVisible={() => {
          setShowSubscriptionAgreement(false)
        }}
        getValues={props.getValues}
      />
      <DataConfirmationModal
        isVisible={showDataConfirmationModal}
        onProceed={handleSubmit}
        onCancel={() => setShowDataConfirmationModal(false)}
        data={props.getValues}
      />
    </div>
  )
}

export default AcknowledgementPage
